import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner'
import RefreshIcon from '@mui/icons-material/Refresh'
import { FC, useCallback, useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { documentoService } from '@/services/documentoService'
import { diagnosticoService } from '@/services/diagnosticoService'
import { Documento } from '@/types/documento'
import { TIPO_ENTIDADE_OPORTUNIDADE } from '@/utils/docsObrigatorios'

interface Props {
  reservaId: string
  /** Rebusca o diagnóstico no pai — é lá que as sugestões (assíncronas) aparecem. */
  onAtualizarSugestoes: () => void
}

/**
 * Bloco "Captura inteligente · OCR" (etapa 1 do workspace): o usuário escolhe um
 * documento JÁ enviado da reserva (ou sobe um novo pelo fluxo de upload existente —
 * quarentena intacta) e dispara a análise. O backend responde 202 e as sugestões chegam
 * de forma assíncrona no GET do diagnóstico; nada é gravado sem o usuário aplicar.
 */
export const CapturaInteligenteOcr: FC<Props> = ({ reservaId, onAtualizarSugestoes }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [documentos, setDocumentos] = useState<Documento[]>([])
  const [documentoId, setDocumentoId] = useState('')
  const [carregando, setCarregando] = useState(true)
  const [enviandoArquivo, setEnviandoArquivo] = useState(false)
  const [analisando, setAnalisando] = useState(false)
  const [analiseEmAndamento, setAnaliseEmAndamento] = useState(false)

  const carregarDocumentos = useCallback(async () => {
    setCarregando(true)
    try {
      const page = await documentoService.listarFiltrado(
        TIPO_ENTIDADE_OPORTUNIDADE, reservaId, 0, 200
      )
      setDocumentos(page.content ?? [])
    } catch {
      // Sem lista de documentos o bloco ainda serve para upload — não é erro fatal.
      setDocumentos([])
    } finally {
      setCarregando(false)
    }
  }, [reservaId])

  useEffect(() => {
    void carregarDocumentos()
  }, [carregarDocumentos])

  const handleUpload = async (file: File) => {
    setEnviandoArquivo(true)
    try {
      const novo = await documentoService.upload(
        file, 'OUTRO', reservaId, TIPO_ENTIDADE_OPORTUNIDADE
      )
      enqueueSnackbar(`${file.name} enviado`, { variant: 'success' })
      await carregarDocumentos()
      if (novo?.id) setDocumentoId(novo.id)
    } catch (e) {
      enqueueSnackbar(
        (e as { message?: string })?.message || 'Falha ao enviar o arquivo',
        { variant: 'error' }
      )
    } finally {
      setEnviandoArquivo(false)
    }
  }

  const analisar = async () => {
    if (!documentoId) return
    setAnalisando(true)
    try {
      await diagnosticoService.sugerir(reservaId, documentoId)
      setAnaliseEmAndamento(true)
      enqueueSnackbar('Análise em andamento — as sugestões aparecem aqui quando prontas', {
        variant: 'info',
      })
    } catch (e) {
      enqueueSnackbar(
        (e as { message?: string })?.message || 'Não foi possível iniciar a análise',
        { variant: 'error' }
      )
    } finally {
      setAnalisando(false)
    }
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Captura inteligente · OCR</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Envie ou escolha um documento da reserva para a IA sugerir o preenchimento do
        diagnóstico. Sugestões são propostas: nada é gravado sem você aplicar e salvar.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
        <TextField
          select
          size="small"
          label="Documento da reserva"
          value={documentoId}
          onChange={(e) => setDocumentoId(e.target.value)}
          disabled={carregando}
          sx={{ minWidth: 260 }}
        >
          {documentos.length === 0 ? (
            <MenuItem value="" disabled>
              Nenhum documento enviado ainda
            </MenuItem>
          ) : (
            documentos.map((doc) => (
              <MenuItem key={doc.id} value={doc.id}>
                {doc.nome}
              </MenuItem>
            ))
          )}
        </TextField>

        <Button
          component="label"
          size="small"
          variant="outlined"
          disabled={enviandoArquivo}
          startIcon={
            enviandoArquivo ? <CircularProgress size={14} color="inherit" /> : <CloudUploadIcon />
          }
        >
          Enviar documento
          <input
            type="file"
            hidden
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.png"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) void handleUpload(f)
              e.target.value = ''
            }}
          />
        </Button>

        <Button
          size="small"
          variant="contained"
          onClick={() => void analisar()}
          disabled={!documentoId || analisando}
          startIcon={
            analisando ? <CircularProgress size={14} color="inherit" /> : <DocumentScannerIcon />
          }
        >
          Analisar documento
        </Button>
      </Stack>

      {analiseEmAndamento && (
        <Alert
          severity="info"
          sx={{ mt: 2 }}
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon />}
              onClick={onAtualizarSugestoes}
            >
              Atualizar sugestões
            </Button>
          }
        >
          Análise em andamento — a IA processa o documento e as sugestões aparecem no
          bloco acima. Atualize em instantes.
        </Alert>
      )}
    </Box>
  )
}

export default CapturaInteligenteOcr
